import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import CheckLoginDialog from "@/components/modal/CheckLoginDialog";
import useDialog from "@/hooks/useDialog";
import { useAuthStore } from "@/lib/auth/AuthStore";
import { mixpanelEvent } from "@/lib/mixpanels/mixpanel";
import useWindowDimensions from "@/lib/web-ui/useWindowDimensions";
import { ConfirmDialog } from "@bookips/solvook-ui-library";
import { styled } from "@mui/material";
import SEO from "@/components/SEO";
import { useMutation } from "@tanstack/react-query";
import _ from "lodash";
import { useRouter } from "next/router";
import { useEffect } from "react";
import { postValidateHandout, validateMembership } from "src/api/landing";
const MIXPANEL_TYPE = {
    event: "Event",
    expert: "eXpert"
};
const dialogMessages = {
    ready: {
        title: "곧 오픈 예정이에요",
        severity: "caution",
        okText: "확인"
    },
    close: {
        title: "이미 종료되었어요.",
        severity: "caution",
        okText: "확인"
    },
    passError: {
        title: "지급 대상이 아닙니다.",
        content: "자세한 내용은 유의사항을 확인해 주세요.",
        severity: "caution",
        okText: "확인"
    },
    passSuccess: {
        content: "이용권 사용방법은 하단 유의사항을 참고하세요.",
        severity: "success",
        okText: "사용하러 가기",
        cancelText: "확인"
    },
    handoutSuccess: {
        title: "무료 자료가 지급됐어요!",
        content: "지급된 자료는 자료 보관함에서 확인할 수 있어요.",
        severity: "success",
        okText: "자료 보관함 가기",
        cancelText: "확인"
    },
    handoutError: {
        title: "자료를 이미 보유하고 있어요.",
        content: "보유한 자료는 자료 보관함에서 확인할 수 있어요.",
        severity: "caution",
        okText: "자료 보관함 가기",
        cancelText: "확인"
    },
    error: {
        title: "오류가 발생했어요.",
        content: "상담 문의 부탁드려요.",
        severity: "caution",
        okText: "확인"
    }
};
const Landing = (param)=>{
    let { landing  } = param;
    const { status , sectionItems  } = landing;
    const { device  } = useWindowDimensions();
    const router = useRouter();
    const { isLoggedIn  } = useAuthStore();
    const { open , dialogProps  } = useDialog();
    const { isOpen: isLoginDialogOpen , open: openLoginDialog , close: closeLoginDialog  } = useDialog();
    const validateMembershipMutation = useMutation(validateMembership);
    const validateHandoutMutation = useMutation(postValidateHandout);
    const mixpanelBase = {
        "Page Type": MIXPANEL_TYPE[landing.type],
        "Page Id": landing.id,
        "Page Title": landing.ogTitle
    };
    // 섹션 클릭 이벤트
    const isProcessing = validateMembershipMutation.isLoading || validateHandoutMutation.isLoading;
    const handleSectionClick = async (section)=>{
        if (isProcessing) return;
        const mixpanelSection = {
            "Section Id": section.id,
            Url: section.imageLink || section.buttonLink,
            "Section Type": section.type
        };
        if (section.imageLink) {
            mixpanelEvent("[PR] Landing Section Clicked", {
                ...mixpanelBase,
                ...mixpanelSection
            });
            window.location.href = section.imageLink;
            return;
        }
        if (section.type === "handout") {
            await validateHandout(section, (mixpanelProperties)=>{
                mixpanelEvent("[PR] Landing Section Clicked", {
                    ...mixpanelBase,
                    ...mixpanelSection,
                    ...mixpanelProperties
                });
            });
        }
        if (section.buttonLink) await validatePass(section, (mixpanelProperties)=>{
            mixpanelEvent("[PR] Landing Section Clicked", {
                ...mixpanelBase,
                ...mixpanelSection,
                ...mixpanelProperties
            });
        });
    };
    // 로그인 확인
    const checkValidation = (sectionId)=>{
        if (!isLoggedIn) throw new Error("Unauthorized");
        if (!sectionId) throw new Error("Unavailable Section Id");
    };
    // 자료 발급
    const validateHandout = async (section, onComplete)=>{
        let mixpanelProperties = {};
        try {
            checkValidation(section.id);
            const response = await validateHandoutMutation.mutateAsync({
                sectionId: section.id
            });
            mixpanelProperties = {
                "Handout  Delivery Completed": response.isIssued ? "Y" : "N",
                "Handout Id": response.issuedProducts.join(","),
                Reason: response.isIssued ? null : "Already Issued"
            };
            const successMessage = response.isIssued ? dialogMessages.handoutSuccess : dialogMessages.handoutError;
            open({
                ...successMessage,
                onOk: ()=>{
                    mixpanelEvent("[PR] Pop-up Button Clicked", {
                        ...mixpanelBase,
                        "Popup Type": "Handout  Delivery Completed",
                        "Popup Title": successMessage.title,
                        "Button Name": successMessage.okText,
                        "Button URL": response.buttonLink
                    });
                    window.location.href = response.buttonLink;
                },
                onCancel: ()=>{
                    mixpanelEvent("[PR] Pop-up Button Clicked", {
                        ...mixpanelBase,
                        "Popup Type": "Handout  Delivery Completed",
                        "Popup Title": successMessage.title,
                        "Button Name": successMessage.cancelText,
                        "Button URL": "null"
                    });
                }
            });
        } catch (error) {
            mixpanelProperties["Pass Issuance Completed"] = "N";
            // 운영상의 이슈 404
            if (error.message === "Unauthorized") {
                mixpanelProperties["Reason"] = "Not Logged In";
                openLoginDialog();
            } else {
                mixpanelProperties["Reason"] = "Unavailable";
                open(dialogMessages.error);
            }
        } finally{
            onComplete(mixpanelProperties);
        }
    };
    // 이용권 발급
    const validatePass = async (section, onComplete)=>{
        let mixpanelProperties = {};
        try {
            checkValidation(section.id);
            const response = await validateMembershipMutation.mutateAsync({
                sectionId: section.id
            });
            mixpanelProperties = {
                "Pass Issuance Completed": "Y",
                "Pass Id": response.membershipProductId
            };
            open({
                ...dialogMessages.passSuccess,
                title: "".concat(response.membershipProductTitle, "이 발급됐어요!"),
                onCancel: ()=>{
                    mixpanelEvent("[PR] Pop-up Button Clicked", {
                        ...mixpanelBase,
                        "Popup Type": "Issuance Completed",
                        "Popup Title": "".concat(response.membershipProductTitle, "이 발급됐어요!"),
                        "Button Name": dialogMessages.passSuccess.cancelText,
                        "Button URL": "null"
                    });
                },
                onOk: ()=>{
                    mixpanelEvent("[PR] Pop-up Button Clicked", {
                        ...mixpanelBase,
                        "Popup Type": "Issuance Completed",
                        "Popup Title": "".concat(response.membershipProductTitle, "이 발급됐어요!"),
                        "Button Name": dialogMessages.passSuccess.okText,
                        "Button URL": section.buttonLink
                    });
                    window.location.href = section.buttonLink;
                }
            });
        } catch (error) {
            ;
            mixpanelProperties["Pass Issuance Completed"] = "N";
            if (error.message === "Unauthorized") {
                mixpanelProperties["Reason"] = "Not Logged In";
                openLoginDialog();
            } else {
                mixpanelProperties["Reason"] = "Unavailable";
                open({
                    ...dialogMessages.passError,
                    onOk: function() {
                        mixpanelEvent("[PR] Pop-up Button Clicked", {
                            ...mixpanelBase,
                            "Popup Type": "Issuance Restricted",
                            "Popup Title": this.title,
                            "Button Name": this.okText,
                            "Button URL": "null"
                        });
                    }
                });
            }
        } finally{
            onComplete(mixpanelProperties);
        }
    };
    useEffect(()=>{
        if (status === "ready") open({
            ...dialogMessages.ready,
            onOk: ()=>router.push("/"),
            onClose: ()=>router.push("/")
        });
        if (status === "close") open({
            ...dialogMessages.close,
            onOk: ()=>router.push("/"),
            onClose: ()=>router.push("/")
        });
    }, []);
    const metadata = Object.assign(_.pick(landing, [
        "ogUrl",
        "ogTitle",
        "keywords",
        "ogDescription",
        "ogImage"
    ]), {
        title: landing.ogTitle,
        description: landing.ogDescription
    });
    return /*#__PURE__*/ _jsxs(Container, {
        children: [
            /*#__PURE__*/ _jsx(SEO, {
                ...metadata
            }),
            status === "open" && sectionItems.map((section, idx)=>{
                const imageSrc = device === "pc" ? section.imageUrlPc : section.imageUrlMobile;
                return /*#__PURE__*/ _jsx("button", {
                    onClick: ()=>handleSectionClick(section),
                    children: /*#__PURE__*/ _jsx("div", {
                        style: {
                            backgroundColor: section.bgColor,
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center"
                        },
                        children: /*#__PURE__*/ _jsx("img", {
                            src: imageSrc,
                            alt: "랜딩 페이지 이미지 ".concat(idx + 1)
                        })
                    })
                }, idx);
            }),
            /*#__PURE__*/ _jsx(ConfirmDialog, {
                ...dialogProps
            }),
            /*#__PURE__*/ _jsx(CheckLoginDialog, {
                open: isLoginDialogOpen,
                onClose: closeLoginDialog,
                onClickLogin: (routePath)=>mixpanelEvent("[PR] Pop-up Button Clicked", {
                        ...mixpanelBase,
                        "Popup Type": "Login or Sign up",
                        "Popup Title": "로그인이 필요해요",
                        "Button Name": "로그인",
                        "Button URL": routePath
                    }),
                onClickSignUp: (routePath)=>mixpanelEvent("[PR] Pop-up Button Clicked", {
                        ...mixpanelBase,
                        "Popup Type": "Login or Sign up",
                        "Popup Title": "로그인이 필요해요",
                        "Button Name": "회원가입",
                        "Button URL": routePath
                    })
            })
        ]
    });
};
const Container = styled("div")((param)=>{
    let { theme  } = param;
    return {
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
        button: {
            border: 0,
            background: "none",
            padding: 0,
            width: "100%"
        },
        img: {
            maxWidth: "100%",
            [theme.breakpoints.up("sm")]: {
                maxWidth: 720
            },
            [theme.breakpoints.up("lg")]: {
                maxWidth: 1200
            }
        }
    };
});
export default Landing;
